import { useState, useCallback } from "react";
import type { Editor } from "@tiptap/react";
import { Link as LinkIcon, Unlink } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BubbleMenuButton } from "./bubble-menu-button";

interface LinkEditPopoverProps {
  editor: Editor;
}

export function LinkEditPopover({ editor }: LinkEditPopoverProps) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");

  const isActive = editor.isActive("link");

  const handleOpen = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        const existingUrl = editor.getAttributes("link").href ?? "";
        setUrl(existingUrl);
      }
      setOpen(nextOpen);
    },
    [editor],
  );

  const applyLink = useCallback(() => {
    const trimmedUrl = url.trim();

    if (!trimmedUrl) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      setOpen(false);
      return;
    }

    if (!isValidLinkHref(trimmedUrl)) return;

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: trimmedUrl })
      .run();
    setOpen(false);
  }, [editor, url]);

  const removeLink = useCallback(() => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setOpen(false);
  }, [editor]);

  // 허용:
  //  - 외부 절대 URL (http/https/mailto)
  //  - 내부 상대 경로 (`/slug`, `/category/slug` 등) — 위키 페이지 간 링크용
  function isValidLinkHref(href: string): boolean {
    if (href.startsWith("/") && !href.startsWith("//")) {
      // 상대 경로: `//evil.com` 같은 protocol-relative URL은 제외 (XSS 우회 차단)
      return true;
    }
    try {
      const parsed = new URL(href);
      return ["http:", "https:", "mailto:"].includes(parsed.protocol);
    } catch {
      return false;
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <div>
          <BubbleMenuButton
            onClick={() => handleOpen(!open)}
            active={isActive}
            aria-label="Link"
          >
            <LinkIcon className="h-3.5 w-3.5" />
          </BubbleMenuButton>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" side="top">
        <div className="flex flex-col gap-2">
          <Input
            placeholder="https://... 또는 /slug (위키 내부 링크)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyLink();
              }
            }}
            className="h-8 text-sm"
          />
          <div className="flex gap-1">
            <Button size="sm" className="h-7 flex-1 text-xs" onClick={applyLink}>
              적용
            </Button>
            {isActive && (
              <Button
                size="sm"
                variant="destructive"
                className="h-7 text-xs"
                onClick={removeLink}
              >
                <Unlink className="mr-1 h-3 w-3" />
                제거
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
